import React, { useEffect, useState } from "react";
import { CalendarClock, Clock, MoonStar, TrendingUp, TrendingDown, Minus, Sparkles, AlertTriangle } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { LoadingBlock } from "@/components/ds";
import { api, type ForecastData } from "@/lib/api";

const DirIcon = ({ d }: { d: string }) =>
  d === "up" ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
    : d === "down" ? <TrendingDown className="h-3.5 w-3.5 text-red-500" />
      : <Minus className="h-3.5 w-3.5 text-slate-400" />;

function Panel({ title, icon: Icon, children, testid }: { title: string; icon: any; children: React.ReactNode; testid: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="h-4 w-4 text-blue-500" /> {title}</div>
      {children}
    </div>
  );
}

export function ForecastsPanel() {
  const [d, setD] = useState<ForecastData | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.forecasts().then(setD).catch(() => setErr(true)); }, []);
  if (err) return null;
  if (!d) return <LoadingBlock rows={3} />;

  const ent = d.entitlements || {};
  const h = d.historical;

  return (
    <div className="space-y-4" data-testid="forecasts-panel">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-slate-900">Call-volume & staffing insights</h2>
        <span className="text-xs text-slate-400">({d.plan} plan · last {d.lookback_days} days · {h.total_calls} calls)</span>
      </div>

      {/* Historical (all plans) */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Busiest days" icon={CalendarClock} testid="forecasts-busiest-days">
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={h.busiest_days}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Busiest hours" icon={Clock} testid="forecasts-busiest-hours">
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={h.busiest_hours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={2} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm" data-testid="forecasts-afterhours">
          <MoonStar className="h-4 w-4 text-indigo-500" /> After-hours calls: <b className="text-slate-900">{h.after_hours_calls}</b>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm">
          Active days with calls: <b className="text-slate-900">{h.active_days}</b>
        </div>
      </div>

      {/* Enhanced trends (growth+) */}
      {ent.trends && d.service_trends && d.service_trends.length > 0 && (
        <Panel title="Service demand trends" icon={TrendingUp} testid="forecasts-service-trends">
          <div className="divide-y divide-slate-100">
            {d.service_trends.map((s) => (
              <div key={s.service} className="flex items-center justify-between py-1.5 text-sm" data-testid={`forecasts-trend-${s.service}`}>
                <span className="text-slate-700">{s.service}</span>
                <span className="flex items-center gap-2 text-slate-600"><DirIcon d={s.direction} /> {s.total}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Predictions (pro+) */}
      {ent.predictions && (
        <>
          {d.has_enough_data === false && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="forecasts-insufficient">
              <AlertTriangle className="mr-1 inline h-4 w-4" /> {d.insufficient_reason}
            </div>
          )}
          {d.forecast && (
            <>
              <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700" data-testid="forecasts-label">
                <Sparkles className="h-4 w-4" /> {d.forecast_label}
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Panel title={`Estimated calls — next ${d.horizon_days} days`} icon={TrendingUp} testid="forecasts-upcoming">
                  <div style={{ width: "100%", height: 200 }}>
                    <ResponsiveContainer>
                      <LineChart data={d.forecast.upcoming}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="weekday" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip /><Line type="monotone" dataKey="predicted_calls" stroke="#2563eb" strokeWidth={2} dot />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
                <Panel title="Recommended coverage windows" icon={Clock} testid="forecasts-coverage">
                  <p className="mb-2 text-xs text-slate-500">Busiest day <b>{d.forecast.busiest_day}</b> · peak hour <b>{d.forecast.busiest_hour}</b></p>
                  {d.forecast.coverage_windows.length === 0 ? (
                    <p className="text-xs text-slate-400">No clear peak windows in your history yet.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {d.forecast.coverage_windows.map((w, i) => (
                        <li key={i} className="flex items-center justify-between rounded bg-slate-50 px-3 py-1.5 text-sm" data-testid={`forecasts-window-${i}`}>
                          <span className="font-medium text-slate-800">{w.start}–{w.end}</span>
                          <span className="text-xs text-slate-500">~{w.avg_calls_per_day} calls/day</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Panel>
              </div>
            </>
          )}
        </>
      )}

      {!ent.trends && (
        <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700" data-testid="forecasts-upsell">
          You're seeing historical charts. Upgrade to <b>Growth</b> for demand trends, or <b>Pro</b> for predictive call-volume and staffing recommendations.
        </p>
      )}
      {ent.trends && !ent.predictions && (
        <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700" data-testid="forecasts-upsell-pro">
          Upgrade to <b>Pro</b> for predictive call-volume forecasts and recommended staff coverage windows.
        </p>
      )}
    </div>
  );
}
